import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, Check, X } from "lucide-react";

const REASON_LABELS = { name: "name match", contact: "contact match" };

/**
 * XeroProjectMappingPanel — Phase 1 spec, Unit C, build step 3: the review
 * queue for auto-mapping CombinedWork projects to Xero Projects
 * (services/deal_xero_matcher.py / routes/xero_projects.py). Shown inside
 * the Xero integration card once connected.
 */
const XeroProjectMappingPanel = () => {
  const [queue, setQueue] = useState({ pending_confirm: [], needs_mapping: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [decidingId, setDecidingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/xero/projects-mapping/queue");
      setQueue(data);
    } catch (e) {
      toast.error("Couldn't load the Xero project mapping queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/xero/projects-mapping/sync");
      toast.success(`Pulled ${data.xero_projects_pulled} Xero project(s) — ${data.matches_created} new match(es) to review`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const confirm = async (matchId) => {
    setDecidingId(matchId);
    try {
      await api.post(`/integrations/xero/projects-mapping/${matchId}/confirm`);
      toast.success("Match confirmed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't confirm match");
    } finally {
      setDecidingId(null);
    }
  };

  const dismiss = async (matchId, noCostsInXero) => {
    setDecidingId(matchId);
    try {
      await api.post(`/integrations/xero/projects-mapping/${matchId}/dismiss`, { no_costs_in_xero: noCostsInXero });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't dismiss match");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <div className="border-t border-[#30363d] pt-4 mt-2" data-testid="xero-project-mapping-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-[#7d8590] uppercase tracking-wider">Project mapping</span>
        <button
          onClick={sync}
          disabled={syncing}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#00F5D4] hover:text-white transition-colors disabled:opacity-50"
          data-testid="sync-xero-projects-btn"
        >
          {syncing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</> : <><RefreshCw className="w-3.5 h-3.5" /> Sync Xero projects</>}
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-[#484f58]">Loading…</div>
      ) : (
        <>
          {queue.pending_confirm.length === 0 && queue.needs_mapping.length === 0 ? (
            <p className="text-xs text-[#484f58]">
              Nothing to review. Run a sync after connecting Xero to match projects against Xero Projects.
            </p>
          ) : (
            <div className="space-y-2">
              {queue.pending_confirm.map((m) => (
                <div
                  key={m.match_id}
                  className="flex items-center justify-between gap-3 bg-[#161b22] border border-[#30363d] rounded-lg px-3 py-2"
                  data-testid={`mapping-row-${m.match_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-xs text-white truncate">
                      {m.project_name} <span className="text-[#484f58]">→</span> {m.xero_project_name}
                    </div>
                    <div className="text-[10px] text-[#7d8590] mt-0.5">
                      {REASON_LABELS[m.reason] || m.reason} · {Math.round(m.score * 100)}% confidence
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => confirm(m.match_id)}
                      disabled={decidingId === m.match_id}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#00F5D4] border border-[#00F5D4]/30 bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 rounded px-2 py-1 disabled:opacity-50"
                      data-testid={`confirm-mapping-${m.match_id}`}
                    >
                      <Check className="w-3 h-3" /> Confirm
                    </button>
                    <button
                      onClick={() => dismiss(m.match_id, true)}
                      disabled={decidingId === m.match_id}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#7d8590] border border-[#30363d] hover:bg-white/5 rounded px-2 py-1 disabled:opacity-50"
                      data-testid={`dismiss-mapping-${m.match_id}`}
                    >
                      <X className="w-3 h-3" /> Not in Xero
                    </button>
                  </div>
                </div>
              ))}
              {queue.needs_mapping.length > 0 && (
                <div className="text-[10px] text-[#484f58] pt-1">
                  {queue.needs_mapping.length} project{queue.needs_mapping.length !== 1 ? "s" : ""} with no Xero match found: {" "}
                  {queue.needs_mapping.map((p) => p.name).join(", ")}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default XeroProjectMappingPanel;
